/**
 * Event LIVENESS — one place that decides whether an event is happening RIGHT NOW,
 * and what its status badge should therefore say.
 *
 * WHY THIS EXISTS
 * ---------------
 * `events.status` is NOT a reliable indicator of liveness and never has been. There is
 * no automation anywhere that moves an event from 'scheduled' to 'in_progress' — the
 * only status automation is `auto_complete_past_active_events` (cron job 55), which
 * moves 'scheduled'/'in_progress' -> 'completed' AFTER an event has ended (mig 1500,
 * 2026-11-01: with or without attendee rows; before that only events with attendees).
 * Every 'in_progress' value in the database was typed in by a human on the event detail page.
 *
 * The consequence, observed on production 2026-09-08: four range nights running at the
 * same instant, all taking check-ins — two badges read "In progress", two read
 * "Scheduled" (one of those with 15 people already checked in). The stored status was
 * simply recording who had remembered to flip a dropdown.
 *
 * THE FIX (owner-chosen): DERIVE the displayed badge from the event's own timestamps.
 * No cron, no writes to `status`, no data churn, no races. Display-only.
 *
 * THE RULE
 * --------
 *   live  <=>  start_time <= now < effectiveCutoff(event)
 *
 * evaluated in the EVENT's own timezone. `effectiveCutoff` is a faithful port of the
 * SQL `public._event_effective_cutoff` (database/migrations/
 * 2026-08-13-001-event-attendance-resolver-and-rpcs.sql), which is the existing single
 * source of the event-timing rule used by the attendance/no-show cohort resolver:
 *
 *   tz     = the stored `timezone` if the runtime recognises it, else America/Chicago
 *   cutoff = end_time
 *            ?? (all_day ? midnight after the event's last local date : start + 6h grace)
 *
 * The 6-hour grace for a timed event with no `end_time`, and the exclusive-midnight
 * treatment of the last local date, are both carried over verbatim rather than
 * reinvented. (See the note on `eventLastLocalDate` about when the midnight rule can
 * actually fire.)
 *
 * WHAT DERIVATION MAY AND MAY NOT OVERRIDE
 * ----------------------------------------
 * Only 'scheduled' and 'in_progress' are candidates. 'cancelled', 'completed' and
 * 'postponed' are explicit human/automated decisions about the event and are passed
 * through untouched — a cancelled event inside its old time window must never light up
 * as live. Anything unrecognised is also passed through.
 *
 * `date_tbd` events, and events with no usable `start_time`, fall back to the stored
 * status: we have nothing to derive from, so we say nothing.
 *
 * STORED 'in_progress' WHOSE WINDOW HAS PASSED
 * --------------------------------------------
 * Shown as "Ended", not "In Progress". 'in_progress' is an active claim that the event
 * is happening NOW; once the window is over that claim is false, and leaving it up is
 * the same class of lie this module exists to remove. It is also transient: cron 55
 * completes such an event on its next hourly pass.
 *
 * Stored 'scheduled' whose window has passed is deliberately LEFT as "Scheduled".
 * "Scheduled" asserts a plan, not a present state, so it is not lying about liveness,
 * and since mig 1500 cron 55 completes every ended event within the hour (zero-activity
 * ones included), so a past 'scheduled' event is short-lived. This asymmetry is deliberate.
 *
 * Stored 'in_progress' BEFORE the start is shown as "Scheduled" for the same reason:
 * liveness is derived, and a manual 'in_progress' is a hint, never proof.
 *
 * NOT AFFECTED: self-check-in eligibility. The picker gates on
 * `status IN ('scheduled','in_progress','postponed')` plus the day-granularity
 * `self_checkin_event_active_today` helper, all server-side. Nothing here writes
 * `status` or is read by that path.
 */

/** Business default when an event has no usable timezone (matches the SQL helpers). */
export const DEFAULT_EVENT_TIMEZONE = "America/Chicago";

/** Documented grace for a TIMED event with no end_time (mirrors _event_effective_cutoff). */
export const NULL_END_TIME_GRACE_MS = 6 * 60 * 60 * 1000;

/** The only stored statuses whose badge may be replaced by a derived one. */
export const DERIVABLE_EVENT_STATUSES: ReadonlySet<string> = new Set([
  "scheduled",
  "in_progress",
]);

/** Minimal shape needed to judge liveness — every event-ish row already has these. */
export interface EventLivenessInput {
  status?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  all_day?: boolean | null;
  timezone?: string | null;
  date_tbd?: boolean | null;
}

export type EventDisplayStatusKey =
  | "live"
  | "ended"
  | "scheduled"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "postponed"
  | "other";

export interface EventDisplayStatus {
  /** Stable key for colour/label mapping. "live"/"ended" are derived-only. */
  key: EventDisplayStatusKey;
  /** Human label for the badge. */
  label: string;
  /** The raw `events.status` this was derived from (normalised, "" when absent). */
  storedStatus: string;
  /** True only when the event is happening right now by its own timestamps. */
  isLive: boolean;
  /** True when the badge deliberately differs from the stored status. */
  derived: boolean;
  /** Tooltip explaining a derived badge (undefined when it just mirrors storage). */
  title?: string;
  /** Tailwind classes for the badge, so every surface colours it identically. */
  className: string;
}

/** Labels for stored statuses passed through untouched. */
const STORED_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  postponed: "Postponed",
};

const BADGE_CLASSES: Record<EventDisplayStatusKey, string> = {
  // Same yellow the manually-set "In progress" badge already used — this is a
  // truthfulness fix, not a restyle.
  live: "bg-yellow-100 text-yellow-800",
  ended: "bg-gray-100 text-gray-800",
  scheduled: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  postponed: "bg-orange-100 text-orange-800",
  other: "bg-gray-100 text-gray-800",
};

/** Badge classes for a display key. Exported so surfaces never hand-roll colours. */
export function eventDisplayStatusClasses(key: EventDisplayStatusKey): string {
  return BADGE_CLASSES[key] ?? BADGE_CLASSES.other;
}

/**
 * The timezone to evaluate an event in: the stored name when the runtime recognises
 * it, else America/Chicago. Mirrors the SQL `pg_timezone_names` validation, which
 * exists so a blank or junk `timezone` can never raise from a display path.
 */
export function resolveEventTimeZone(tz?: string | null): string {
  if (!tz) {
    return DEFAULT_EVENT_TIMEZONE;
  }
  try {
    // Throws RangeError for an unknown/invalid IANA name.
    new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date(0));
    return tz;
  } catch {
    return DEFAULT_EVENT_TIMEZONE;
  }
}

interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
}

/** Wall-clock parts of an instant, in the given timezone. */
function localParts(ms: number, tz: string): LocalParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const read = (type: string): number => {
    const found = parts.find((p) => p.type === type);
    return found ? Number(found.value) : 0;
  };
  // Some runtimes emit hour "24" for midnight under hour12:false.
  const hour = read("hour") % 24;
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour,
    minute: read("minute"),
    second: read("second"),
  };
}

/** The timezone's UTC offset (ms) at a given instant. */
function tzOffsetMs(ms: number, tz: string): number {
  const p = localParts(ms, tz);
  const asIfUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  // Drop sub-second precision on both sides so the difference is a clean offset.
  return asIfUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * The UTC instant for a wall-clock moment in `tz`. Two passes converge across DST
 * transitions (the offset at the guess may differ from the offset at the answer).
 */
function wallClockToUtcMs(
  year: number,
  month: number, // 1-12
  day: number,
  hour: number,
  minute: number,
  tz: string,
): number {
  const naive = Date.UTC(year, month - 1, day, hour, minute, 0);
  let ms = naive;
  for (let i = 0; i < 2; i += 1) {
    ms = naive - tzOffsetMs(ms, tz);
  }
  return ms;
}

function parseInstant(iso?: string | null): number | null {
  if (!iso) {
    return null;
  }
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The last calendar date the event covers, in its own timezone, as {year, month, day}.
 *
 * Port of the SQL: derived from COALESCE(end_time, start_time), with an exact local
 * midnight treated as EXCLUSIVE (a stored next-midnight end means the last active local
 * day is the day before) — but only when that end is strictly after the start.
 *
 * NOTE, honestly: in `_event_effective_cutoff` this value is only consumed on the
 * all_day-with-NULL-end_time branch, where COALESCE resolves to `start_time` and the
 * `> start_time` guard is therefore false — so the midnight subtraction cannot fire
 * there. It is carried over verbatim (and tested directly here) so this helper stays a
 * faithful port and inherits any future correction to the SQL, rather than quietly
 * diverging. The same exclusive-midnight rule DOES fire for real in
 * `self_checkin_event_active_today`.
 */
export function eventLastLocalDate(
  event: EventLivenessInput,
): { year: number; month: number; day: number } | null {
  const start = parseInstant(event.start_time);
  if (start === null) {
    return null;
  }
  const tz = resolveEventTimeZone(event.timezone);
  const end = parseInstant(event.end_time);
  const anchor = end ?? start;
  const p = localParts(anchor, tz);
  const exclusiveMidnight =
    p.hour === 0 && p.minute === 0 && p.second === 0 && anchor > start;
  if (!exclusiveMidnight) {
    return { year: p.year, month: p.month, day: p.day };
  }
  const prev = new Date(Date.UTC(p.year, p.month - 1, p.day));
  prev.setUTCDate(prev.getUTCDate() - 1);
  return {
    year: prev.getUTCFullYear(),
    month: prev.getUTCMonth() + 1,
    day: prev.getUTCDate(),
  };
}

/**
 * The instant an event stops being "happening now", as a UTC epoch-ms.
 * Faithful port of SQL `public._event_effective_cutoff`. Null when the event has no
 * usable start_time.
 */
export function eventEffectiveCutoffMs(
  event: EventLivenessInput,
): number | null {
  const start = parseInstant(event.start_time);
  if (start === null) {
    return null;
  }
  const end = parseInstant(event.end_time);
  if (end !== null) {
    return end; // end_time always wins (COALESCE's first argument)
  }
  if (event.all_day) {
    const tz = resolveEventTimeZone(event.timezone);
    const last = eventLastLocalDate(event);
    if (!last) {
      return null;
    }
    // Midnight AFTER the last local date, i.e. end of that date.
    const next = new Date(Date.UTC(last.year, last.month - 1, last.day));
    next.setUTCDate(next.getUTCDate() + 1);
    return wallClockToUtcMs(
      next.getUTCFullYear(),
      next.getUTCMonth() + 1,
      next.getUTCDate(),
      0,
      0,
      tz,
    );
  }
  return start + NULL_END_TIME_GRACE_MS; // documented grace
}

/** Convenience wrapper returning a Date (or null). */
export function eventEffectiveCutoff(event: EventLivenessInput): Date | null {
  const ms = eventEffectiveCutoffMs(event);
  return ms === null ? null : new Date(ms);
}

/**
 * Is this event happening RIGHT NOW by its own timestamps?
 *
 *   start_time <= now < effectiveCutoff
 *
 * Deliberately IGNORES the stored status — callers that must respect an explicit
 * cancelled/completed/postponed decision should use `deriveEventDisplayStatus`, which
 * applies that precedence. `date_tbd` and a missing/unparseable start_time are false.
 */
export function isEventLiveNow(
  event: EventLivenessInput,
  now: Date | number = Date.now(),
): boolean {
  if (event.date_tbd) {
    return false;
  }
  const start = parseInstant(event.start_time);
  if (start === null) {
    return false;
  }
  const cutoff = eventEffectiveCutoffMs(event);
  if (cutoff === null) {
    return false;
  }
  const nowMs = typeof now === "number" ? now : now.getTime();
  return start <= nowMs && nowMs < cutoff;
}

/**
 * Render a stored status as-is: the badge the events list has always shown for
 * anything derivation must not touch. An empty status reads "Scheduled", which is
 * what the list did before this change; an unrecognised one is title-cased and
 * given the neutral badge rather than being dropped.
 */
function passThrough(stored: string): EventDisplayStatus {
  if (!stored) {
    return {
      key: "scheduled",
      label: STORED_LABELS.scheduled,
      storedStatus: stored,
      isLive: false,
      derived: false,
      className: eventDisplayStatusClasses("scheduled"),
    };
  }
  // "canceled" is the alternate spelling seen in a few older rows.
  const candidate = (
    stored === "canceled" ? "cancelled" : stored
  ) as EventDisplayStatusKey;
  const resolvedKey: EventDisplayStatusKey =
    Object.prototype.hasOwnProperty.call(BADGE_CLASSES, candidate)
      ? candidate
      : "other";
  const label =
    STORED_LABELS[stored] ??
    stored.charAt(0).toUpperCase() + stored.slice(1).replace(/_/g, " ");
  return {
    key: resolvedKey,
    label,
    storedStatus: stored,
    isLive: false,
    derived: false,
    className: eventDisplayStatusClasses(resolvedKey),
  };
}

/**
 * THE badge decision. Every surface that shows an event's status must call this.
 *
 * Precedence:
 *   1. Explicit/terminal stored status (anything other than scheduled/in_progress)
 *      wins outright and is never overridden.
 *   2. date_tbd, or no usable start_time -> fall back to the stored status.
 *   3. Otherwise derive from the timestamps: live -> "Happening now";
 *      before start -> "Scheduled"; after the window -> "Ended" for a stored
 *      'in_progress', "Scheduled" for a stored 'scheduled'.
 */
export interface DeriveEventDisplayOptions {
  /**
   * Show a stored 'scheduled' event whose window has passed as "Ended" instead of "Scheduled".
   * Off by default (see the asymmetry note above for why the CRM lists keep "Scheduled"); the MSR
   * schedule's Past tab turns it on, because a rep looking back at last week's event needs "it's over",
   * not a plan (MSR QA notes 2026-10-19, item 1).
   */
  pastScheduledAsEnded?: boolean;
}

export function deriveEventDisplayStatus(
  event: EventLivenessInput,
  now: Date | number = Date.now(),
  options: DeriveEventDisplayOptions = {},
): EventDisplayStatus {
  const stored = (event.status ?? "").toLowerCase().trim();

  // 1. Explicit decisions (cancelled / completed / postponed / anything unknown) win.
  if (stored && !DERIVABLE_EVENT_STATUSES.has(stored)) {
    return passThrough(stored);
  }

  // 2. Nothing to derive from.
  if (event.date_tbd) {
    return passThrough(stored);
  }
  const start = parseInstant(event.start_time);
  const cutoff = eventEffectiveCutoffMs(event);
  if (start === null || cutoff === null) {
    return passThrough(stored);
  }

  // 3. Derive.
  const nowMs = typeof now === "number" ? now : now.getTime();
  const base = passThrough(stored);

  if (start <= nowMs && nowMs < cutoff) {
    return {
      key: "live",
      label: "Happening now",
      storedStatus: stored,
      isLive: true,
      derived: stored !== "in_progress",
      title:
        stored === "in_progress"
          ? "This event is inside its scheduled window right now."
          : `This event is inside its scheduled window right now (saved status: ${base.label}).`,
      className: eventDisplayStatusClasses("live"),
    };
  }

  if (
    nowMs >= cutoff &&
    (stored === "in_progress" ||
      (options.pastScheduledAsEnded === true && stored === "scheduled"))
  ) {
    return {
      key: "ended",
      label: "Ended",
      storedStatus: stored,
      isLive: false,
      derived: true,
      title:
        stored === "in_progress"
          ? "This event's scheduled window has passed; it is still saved as In Progress."
          : "This event's scheduled window has passed; it is still saved as Scheduled.",
      className: eventDisplayStatusClasses("ended"),
    };
  }

  if (nowMs < start && stored === "in_progress") {
    return {
      key: "scheduled",
      label: "Scheduled",
      storedStatus: stored,
      isLive: false,
      derived: true,
      title:
        "This event has not reached its start time yet; it is saved as In Progress.",
      className: eventDisplayStatusClasses("scheduled"),
    };
  }

  return base;
}
